import React, { useEffect, useState } from 'react';
import Waypoint from 'react-waypoint';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import PropTypes from 'prop-types';
import RichTextDisplay from 'Components/UI/RichTextDisplay';
import { Typography } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import SaveIcon from '@material-ui/icons/Save';
import Error from '@material-ui/icons/Error';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import NoteWithContextMenu
  from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/AllNotes/AllNotes/NoteWithContextMenu';
import useRefDimensions from 'Hooks/useRefDimensions';
import useWindowSize from 'Hooks/useWindowSize';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import { useNoteListDispatchContext } from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/AllNotes/AllNotes/NoteListContext';
import useResource from 'Hooks/useResource';
import ScrollIntoView from 'Components/UI/ScrollIntoView';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { openModal } from 'Helpers/ModalHelper';
import modalConsts from 'Consts/ModalNames';
import { HistoryOutlined } from '@material-ui/icons';

const styles = theme => ({
  root: {
    display: 'flex',
    flex: 7,
    padding: theme.spacing(2, 1),
  },
  note: {
    padding: theme.spacing(0, 0),
    margin: theme.spacing(0.25, 0.25),
  },
  noteSubject: {},
  noteEditor: {},
  hidden: {
    display: 'none',
  },
  hovered: {
    borderWidth: theme.spacing(0.25),
    borderStyle: 'solid',
    borderColor: theme.palette.facet.accentBlue,
    borderRadius: theme.spacing(1),
    margin: theme.spacing(0, 0),
  },
  clickToEdit: {
    color: 'white',
    backgroundColor: theme.palette.facet.accentBlue,
    display: 'block',
    textAlign: 'center',
  },
  clickToEditHidden: {
    visibility: 'hidden',
  },
  noteHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  actions: {
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-end',
  },
  icon: {
    padding: theme.spacing(0),
    height: '27px',
    width: '27px',
  },
  errorIcon: {
    color: theme.palette.facet.red,
  },
  iconButton: {
    padding: theme.spacing(0),
  },
});

const Note = ({
  classes,
  setSelectedNote,
  noteResource,
  setEditNote,
  editNote,
  setScrollTo,
  scrollTo,
}) => {
  const [hoveredNote, setHoveredNote] = useState('');
  const [ref, dimensions] = useRefDimensions([...useWindowSize(), noteResource]);
  const [saved, setSaved] = useState('saved');
  const { resource, setResourceDeprecated: setResource } = useResource(noteResource);
  const listDispatch = useNoteListDispatchContext();
  const [noteHeight, setNoteHeight] = useState(dimensions.height);
  const {
    rootNodeProps,
    logEvent,
  } = useEventTrackingLogEvent({
    nodeName: 'All Notes Note',
  });

  const [content] = useState(noteResource?.attr('content'));

  const LOADING_ICON_SIZE = 27; // Typograph size is 27px

  useEffect(() => {
    if (dimensions.height !== 0) {
      setNoteHeight(dimensions.height);
    }
  }, [dimensions]);

  const allowHover = editNote !== `Note-${noteResource.id()}`;

  const handleSaveNote = async (resourceToSave) => {
    try {
      setSaved('saving'); // Indicate that we are in the saving process
      const savedResourceCollection = await resourceToSave.submit();
      setSaved((prevState) => {
        // If the user has not updated the state at all before this runs then set it to saved.
        if (prevState === 'saving') {
          return 'saved';
        }

        // Otherwise make a pass through, as they have continued typing
        return prevState;
      }); // Indicate that we are in the saving process
      setResource(savedResourceCollection.resources().head());
      listDispatch({ type: 'updateResource', resource: savedResourceCollection.resources().head() });
    } catch (err) {
      setSaved('error'); // Indicate that we are not saved since the save wasn't successful
      switch (err.status) {
        case 409:
          // TODO: Remove once better error messages are implemented
          displayErrorMessage(
            err,
            'Something has gone wrong and the server is trying to create a new note unsuccessfully. Please copy/paste your note elsewhere (for safe keeping) and contact #appsupport.',
          );
          break;
        default:
          displayErrorMessage(err, err.errors);
      }
    }
  };

  const saveNoteAndExit = async () => {
    await handleSaveNote(resource);
    setEditNote('');
  };

  let saveButtonProps = {
    color: 'primary',
    disabled: false,
    icon: SaveIcon,
    isError: false,
  };

  switch (saved) {
    case 'saving':
      saveButtonProps = {
        ...saveButtonProps,
        disabled: true,
        text: 'Saving...',
        icon: LoadingIndicator,
        isError: false,
      };
      break;
    case 'unsaved':
      saveButtonProps = {
        ...saveButtonProps,
        text: 'Save Note',
        icon: SaveIcon,
        isError: false,
      };
      break;
    case 'error':
      saveButtonProps = {
        ...saveButtonProps,
        color: 'inherit',
        isError: true,
        icon: Error,
      };
      break;
    case 'saved':
      saveButtonProps = {
        ...saveButtonProps,
        disabled: false,
        text: 'Note Saved',
        icon: SaveIcon,
        isError: false,
      };
      break;
    default:
      throw new Error('invalid saved state');
  }

  const Icon = saveButtonProps.icon;

  return (
    <div
      className={classNames(
        classes.note,
        hoveredNote === `Note-${noteResource.id()}` && allowHover ? classes.hovered : {},
      )}
      onClick={() => {
        logEvent('Clicked to edit note');
        setEditNote(`Note-${noteResource.id()}`);
      }}
      onKeyPress={() => {
        logEvent('Hit enter to edit note');
        setEditNote(`Note-${noteResource.id()}`);
      }}
      onMouseEnter={() => setHoveredNote(`Note-${noteResource.id()}`)}
      onMouseLeave={() => setHoveredNote('')}
      role="button"
      tabIndex={0}
      id={`Note-${noteResource.id()}`}
      {...rootNodeProps}
    >
      {scrollTo === `Note-${noteResource.id()}` && <ScrollIntoView afterScroll={() => setScrollTo('')} />}
      <div className={classes.noteHeader}>
        <Typography variant="h2" className={classes.noteSubject}>{noteResource.attr('noteSubject')}</Typography>
        {noteResource.id() !== '0' && (
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openModal(modalConsts.historyFlyout, [
                { title: btoa('Note History') },
                { link: btoa(noteResource.relationship('history').link('related').url()) },
              ]);
            }}
          >
            <HistoryOutlined />
          </IconButton>
        )}
        <div className={classes.actions}>
          <IconButton
            color={saveButtonProps.color}
            disabled={!resource.isValid() || saveButtonProps.disabled}
            onClick={(e) => {
              e.stopPropagation();
              logEvent('Clicked save note and exit button');
              saveNoteAndExit();
            }}
            className={classNames(classes.iconButton, allowHover ? classes.hidden : {})}
          >
            <Icon fontSize="large" size={LOADING_ICON_SIZE} className={classNames(classes.icon, saveButtonProps.isError ? classes.errorIcon : {})} />
          </IconButton>
        </div>
      </div>
      <Waypoint
        onEnter={(props) => setSelectedNote(`Note-${noteResource.id()}`, 'enter', noteResource.attr('noteSubject'), props)}
        onLeave={(props) => setSelectedNote(`Note-${noteResource.id()}`, 'leave', noteResource.attr('noteSubject'), props)}
        bottomOffset="95%"
        fireOnRapidScroll
      />
      <RichTextDisplay className={classNames(editNote === `Note-${noteResource.id()}` ? classes.hidden : {})} content={noteResource.attr('content')} ref={ref} />
      <NoteWithContextMenu
        className={classNames(classes.noteEditor, editNote !== `Note-${noteResource.id()}` ? classes.hidden : {})}
        noteResource={noteResource}
        height={noteHeight < 225 ? 225 : noteHeight}
        setSaved={setSaved}
        handleSaveNote={handleSaveNote}
        setResource={setResource}
        onBlur={() => {
          logEvent('Clicked outside of edit note container and caused a onBlur save');
          saveNoteAndExit();
        }}
        content={content}
      />
      <Typography
        variant="h4"
        className={classNames(
          classes.clickToEdit,
          hoveredNote !== `Note-${noteResource.id()}` || !allowHover ? classes.clickToEditHidden : {},
        )}
      >
        {'Click to Edit'}
      </Typography>
    </div>
  );
};

Note.propTypes = {
  classes: PropTypes.object.isRequired,
  setSelectedNote: PropTypes.func.isRequired,
  noteResource: PropTypes.object.isRequired,
  setEditNote: PropTypes.func.isRequired,
  editNote: PropTypes.string.isRequired,
  scrollTo: PropTypes.string.isRequired,
  setScrollTo: PropTypes.func.isRequired,
};

export default withStyles(styles)(Note);
